import { useState } from 'react';

import Button from '../../../../../components/Button/Button';
import Figure from '../../../../../components/Figure/Figure';
import Inline from '../../../../../components/Inline/Inline';
import InlineIcon from '../../../../../components/InlineIcon/InlineIcon';
import Paragraph from '../../../../../components/Paragraph/Paragraph';
import Row from '../../../../../components/Row/Row';
import useTrans from '../../../../../hooks/ui/useTrans';
import BoatModelWithMedia from '../../../../types/BoatModelWithMedia';
import placeholderImage from '../../graphics/boatModelPlaceholder.svg';
import passengersIcon from '../../graphics/passengersIcon.svg?raw';
import NumberOfBoatsSelect from '../NumberOfBoatsSelect/NumberOfBoatsSelect';
import OnlySingleOrMultiDayMessage from '../OnlySingleOrMultiDayMessage/OnlySingleOrMultiDayMessage';
import TintOnHoverButton from '../TintOnHoverButton/TintOnHoverButton';

import styles from './BoatModelCard.module.css';

type BoatModelCardProps = {
    boatModel: BoatModelWithMedia;
    onSelect: (boatModel: BoatModelWithMedia, quantity: number) => void;
    onPreview: (boatModel: BoatModelWithMedia) => void;
    incompatibleSingleOrMultiDay?: boolean;
};

const BoatModelCard = ({
    boatModel,
    onSelect,
    onPreview,
    incompatibleSingleOrMultiDay,
}: BoatModelCardProps) => {
    const [quantity, setQuantity] = useState(1);
    const trans = useTrans('CustomerArea.BookingForm.BoatModelCard.');

    const thumbPath = boatModel.media[0]?.variants.thumb.path;

    const handleSelect = () => onSelect(boatModel, quantity);
    const handleFigureClicked = () => onPreview(boatModel);

    return (
        <div className={styles.Root}>
            <TintOnHoverButton
                onClick={handleFigureClicked}
                borderRadius="top"
                fullWidth
                disabled={incompatibleSingleOrMultiDay}
            >
                <Figure
                    borderRadius="top"
                    defaultPlaceholderRatio={0.5}
                    caption={
                        <Row horizontal="justify" vertical="bottom">
                            <Paragraph lineHeight="small" flex>
                                {boatModel.name}
                            </Paragraph>
                            <Row vertical="middle" space="small">
                                <InlineIcon src={passengersIcon} />
                                <Paragraph bold lineHeight="small">
                                    {boatModel.capacity}
                                </Paragraph>
                            </Row>
                        </Row>
                    }
                    captionPosition="inside"
                    src={thumbPath ?? placeholderImage}
                    vignette={thumbPath ? 'bottomLeft' : undefined}
                    saturation={incompatibleSingleOrMultiDay ? 0 : undefined}
                />
            </TintOnHoverButton>

            <div className={styles.Content}>
                <Paragraph size="small" className={styles.Description}>
                    {boatModel.shortDescription}
                </Paragraph>

                <Inline horizontal="right" vertical="middle">
                    {boatModel.maxSelectableBoats && (
                        <NumberOfBoatsSelect
                            variant="subtle-no-border"
                            max={boatModel.maxSelectableBoats}
                            value={quantity}
                            onChange={setQuantity}
                            size="small"
                        />
                    )}
                    <Button
                        onClick={handleSelect}
                        disabled={incompatibleSingleOrMultiDay}
                    >
                        {trans('ChooseButton')}
                    </Button>
                </Inline>
            </div>

            <OnlySingleOrMultiDayMessage
                show={!!incompatibleSingleOrMultiDay}
                singleOrMultiDay={boatModel.singleOrMultiDay}
            />
        </div>
    );
};

export default BoatModelCard;
